import { Pagination } from './pagination.model';

export type MlValidationFeedbackStatus = 'ACTIVE' | 'NOT_ACTIVE' | 'CHURNED' | 'EXPIRED';
export type MlValidationFeedbackTrialStatus = 'NONE' | 'ACTIVE' | 'CONVERTED' | 'EXPIRED';
/** Confusion-matrix labels produced by the ML model. */
export type MlValidationConfusionMatrixResult = 'TP' | 'FP' | 'FN' | 'TN';
export type MlValidationReviewerResult = 'Correct' | 'Incorrect' | 'TP' | 'FP';
export type MlValidationFeedbackReviewStatus = 'Open' | 'In Review' | 'Needs Investigation' | 'Closed';
export type MlValidationFeedbackOutcomeType = 'FP' | 'Fault Type' | 'Root cause Analysis' | 'Recommendation';
export type MlValidationFeedbackPredictionType = 'compressor_shutdown' | 'root_cause_analysis' | 'recommendation';
export type MlValidationFeedbackObservationCategory =
  | 'Correct Prediction-Resolved Before Happening'
  | 'Maintenance'
  | 'Sensor Failure'
  | 'Data Issue'
  | 'Performance Change'
  | 'False Alarm'
  | 'Emergency Impact'
  | 'Unknown'
  | 'Other';

export interface MlValidationPatchableRowFields {
  reviewerResult?: MlValidationReviewerResult;
  predictionUseful?: boolean;
  actionable?: boolean;
  observationCategory?: MlValidationFeedbackObservationCategory;
  feedback?: string;
  reviewStatus?: MlValidationFeedbackReviewStatus;
  attachments?: string[]; // array of file paths or URLs
}
export interface MlValidationFeedbackRow extends MlValidationPatchableRowFields {
  id: number; // physical DB row id — used to target PATCH /ml-validation-feedbacks/{id}
  predictionType: MlValidationFeedbackPredictionType;
  predictionScore: number;
  predictedOutcome: string | number | null;
  validationResult: MlValidationConfusionMatrixResult;
  actualOutcome?: MlValidationFeedbackOutcomeType;
  actualOutcomeAdditionalInfo?: string;
}

export interface MlValidationFeedback {
  id: number;
  customer: string;
  eventId: string;
  omniId: number;
  component: string;
  predictionTimestamp: Date | string;
  feedbackTimestamp: Date | string | null;
  reviewer?: string; // user.email
  reviewStatus: MlValidationFeedbackReviewStatus;
  rows: MlValidationFeedbackRow[];
}

/** Partial row payload sent to PATCH /ml-validation-feedbacks/:id — id and predictionType are always required. */
export type MlValidationFeedbackRowPatch = Partial<MlValidationFeedbackRow> &
  Pick<MlValidationFeedbackRow, 'id' | 'predictionType'>;

/** Partial feedback payload sent to PATCH /ml-validation-feedbacks/:id — id is always required; rows, when present, use MlValidationFeedbackRowPatch. */
export type MlValidationFeedbackPatch = Omit<Partial<MlValidationFeedback>, 'rows'> &
  Pick<MlValidationFeedback, 'id'> & { rows?: MlValidationFeedbackRowPatch[] };

export interface MlValidationFeedbacksPage {
  total: number;
  data: MlValidationFeedback[];
}

export interface MlValidationFeedbacksStateModel {
  mlValidationFeedbacks: MlValidationFeedback[];
  total: number;
  selected?: MlValidationFeedback;
  isLoading: boolean;
  error?: string;
  success?: string;
  pagination: Pagination;
}

export const ML_VALIDATION_REVIEWER_RESULTS: MlValidationReviewerResult[] = ['Correct', 'Incorrect', 'TP', 'FP'];
export const ML_VALIDATION_REVIEWER_STATUSES: MlValidationFeedbackReviewStatus[] = [
  'Open',
  'In Review',
  'Needs Investigation',
  'Closed',
];

export const ML_VALIDATION_OBSERVATION_CATEGORIES: MlValidationFeedbackObservationCategory[] = [
  'Correct Prediction-Resolved Before Happening',
  'Maintenance',
  'Sensor Failure',
  'Data Issue',
  'Performance Change',
  'False Alarm',
  'Emergency Impact',
  'Unknown',
  'Other',
];
